/**
 * Scenarios – shared types, constants, and scope helpers.
 *
 * Scope (fiscalYear + departmentCode) is derived from URL search params at the
 * page level and threaded down as a `ScenarioScope` object. Nothing here
 * hard-codes those values; the page supplies defaults when params are absent.
 */

import type { ScenarioDriverType } from "@workspace/api-client-react";

// companyId is supplied by WorkspaceCompanyProvider from URL / host context.

// ─── Actor ────────────────────────────────────────────────────────────────────
export const ACTOR = "controller@tekledger.app";

// ─── Defaults (used when URL params are absent) ────────────────────────────────
export const DEFAULT_FISCAL_YEAR = 2026;
export const DEFAULT_DEPARTMENT_CODE = "643";

// ─── Scope type ───────────────────────────────────────────────────────────────
export interface ScenarioScope {
  companyId: number;
  fiscalYear: number;
  departmentCode: string;
}

// ─── Driver form state ─────────────────────────────────────────────────────────
export type DriverFormState = {
  type: ScenarioDriverType;
  name: string;
  glAccount: string;
  // period range (spend_reduction, spend_increase, usage_volume, price_rate,
  // headcount, project, commitment, vendor_consolidation, new_investment)
  startPeriod: string;
  endPeriod: string;
  // timing_shift / fiscal_year_shift source period
  fromPeriod: string;
  // timing_shift destination period (same FY) / fiscal_year_shift destination period (different FY)
  toPeriod: string;
  // fiscal_year_shift destination year
  targetFiscalYear: string;
  // dollar amount (most types support this)
  amount: string;
  // percentage rate (alternative to amount where supported)
  percentage: string;
  // headcount / usage_volume: unit count change
  quantityDelta: string;
  // usage_volume: cost per unit
  unitRate: string;
  // headcount: annual salary
  annualSalary: string;
  // headcount: benefit load %
  benefitRate: string;
  // source-backed types: comma-separated references (commitment requires these)
  sourceReferences: string;
};

export const defaultDriverForm = (type: ScenarioDriverType): DriverFormState => ({
  type,
  name: "",
  glAccount: "",
  startPeriod: "",
  endPeriod: "",
  fromPeriod: "",
  toPeriod: "",
  targetFiscalYear: "",
  amount: "",
  percentage: "",
  quantityDelta: "",
  unitRate: "",
  annualSalary: "",
  benefitRate: "",
  sourceReferences: "",
});

// ─── Field descriptor ─────────────────────────────────────────────────────────
export type DriverFieldDescriptor = {
  key: keyof DriverFormState;
  label: string;
  hint?: string;
  type?: "number" | "text";
  required?: boolean;
};

export type DriverFieldsConfig = {
  label: string;
  /** One-line description shown in the form header. */
  description: string;
  fields: DriverFieldDescriptor[];
};

// ─── Per-type field configuration ─────────────────────────────────────────────
//
// Semantic notes that informed the field choices:
//
// spend_reduction / spend_increase
//   amount OR percentage (engine picks the one supplied; both optional but at
//   least one must be provided).
//
// timing_shift
//   fromPeriod → toPeriod within the *same* fiscal year; amount is the dollars
//   moved. No cross-year transfer here — use fiscal_year_shift for that.
//
// fiscal_year_shift
//   fromPeriod (source period in the current FY) → targetFiscalYear + toPeriod
//   (destination year and period). amount is the dollars moved.
//
// usage_volume
//   quantityDelta (signed integer) × unitRate = monthly impact.
//
// price_rate
//   percentage OR amount (flat delta per unit); engine applies to existing volume.
//
// headcount
//   quantityDelta is the head-count delta (positive = adds, negative = removes).
//   startPeriod / endPeriod bound the active months; the engine preserves
//   already-active months and pro-rates over the supplied range — it does NOT
//   guarantee even distribution.
//   annualSalary / benefitRate are the costing inputs.
//
// commitment
//   sourceReferences are required for source-backed drivers; the engine will
//   reject the driver if they are missing and the commitment is source-backed.
//   These references must point to an immutable snapshot (contract ID, PO ref).
//
// new_investment
//   Represents net-new spend not in the baseline. amount is the explicit total.
//   Explicit monthly allocations are optional; when omitted, the total is spread
//   evenly across the selected period range. Approval references are required.
//
// vendor_consolidation
//   amount OR percentage savings; sourceReferences point to vendor agreements.

export const DRIVER_CONFIGS: Partial<Record<ScenarioDriverType, DriverFieldsConfig>> = {
  spend_reduction: {
    label: "Spend Reduction",
    description:
      "Reduce a cost line by a fixed dollar amount or percentage across a period range. Supply amount or percentage — not both.",
    fields: [
      { key: "glAccount", label: "GL Account", hint: "Restrict to one account (optional)" },
      { key: "startPeriod", label: "Start Period", type: "number", hint: "1–12" },
      { key: "endPeriod", label: "End Period", type: "number", hint: "1–12" },
      { key: "amount", label: "Amount ($)", type: "number", hint: "Fixed dollar reduction (alternative to %)" },
      { key: "percentage", label: "Percentage (%)", type: "number", hint: "% reduction applied to baseline (alternative to $)" },
      { key: "sourceReferences", label: "Source References", hint: "Comma-separated references (optional — e.g. contract-id, PO-123)" },
    ],
  },
  spend_increase: {
    label: "Spend Increase",
    description:
      "Increase a cost line by a fixed dollar amount or percentage. Supply amount or percentage — not both.",
    fields: [
      { key: "glAccount", label: "GL Account", hint: "Restrict to one account (optional)" },
      { key: "startPeriod", label: "Start Period", type: "number", hint: "1–12" },
      { key: "endPeriod", label: "End Period", type: "number", hint: "1–12" },
      { key: "amount", label: "Amount ($)", type: "number", hint: "Fixed dollar increase (alternative to %)" },
      { key: "percentage", label: "Percentage (%)", type: "number", hint: "% increase applied to baseline (alternative to $)" },
      { key: "sourceReferences", label: "Source References", hint: "Comma-separated references (optional)" },
    ],
  },
  timing_shift: {
    label: "Timing Shift",
    description:
      "Move spend from one period to another within the same fiscal year. Use Fiscal Year Shift to move spend across years.",
    fields: [
      { key: "glAccount", label: "GL Account", hint: "Restrict to one account (optional)" },
      { key: "fromPeriod", label: "From Period", type: "number", hint: "Source period in current FY (1–12)", required: true },
      { key: "toPeriod", label: "To Period", type: "number", hint: "Destination period in current FY (1–12)", required: true },
      { key: "amount", label: "Amount ($)", type: "number", hint: "Dollars moved between periods", required: true },
    ],
  },
  fiscal_year_shift: {
    label: "Fiscal Year Shift",
    description:
      "Defer or accelerate spend across fiscal years. Moves dollars from a source period in the current FY to a destination period in another FY.",
    fields: [
      { key: "glAccount", label: "GL Account", hint: "Restrict to one account (optional)" },
      { key: "fromPeriod", label: "From Period", type: "number", hint: "Source period in current FY (1–12)", required: true },
      { key: "targetFiscalYear", label: "Target Fiscal Year", type: "number", hint: "Destination FY (e.g. 2027)", required: true },
      { key: "toPeriod", label: "To Period", type: "number", hint: "Destination period in target FY (1–12)", required: true },
      { key: "amount", label: "Amount ($)", type: "number", hint: "Dollars moved to target FY/period", required: true },
    ],
  },
  usage_volume: {
    label: "Usage / Volume",
    description:
      "Model a change in usage quantity applied to a fixed unit rate. Impact = quantityDelta × unitRate per active month.",
    fields: [
      { key: "glAccount", label: "GL Account", hint: "Restrict to one account (optional)" },
      { key: "startPeriod", label: "Start Period", type: "number", hint: "1–12" },
      { key: "endPeriod", label: "End Period", type: "number", hint: "1–12" },
      { key: "quantityDelta", label: "Quantity Delta", type: "number", hint: "Signed unit change (negative = reduction)", required: true },
      { key: "unitRate", label: "Unit Rate ($)", type: "number", hint: "Cost per unit (must be positive)", required: true },
    ],
  },
  price_rate: {
    label: "Price / Rate",
    description:
      "Model a rate or price change applied to existing volume. Supply percentage or flat rate delta — not both.",
    fields: [
      { key: "glAccount", label: "GL Account", hint: "Restrict to one account (optional)" },
      { key: "startPeriod", label: "Start Period", type: "number", hint: "1–12" },
      { key: "endPeriod", label: "End Period", type: "number", hint: "1–12" },
      { key: "percentage", label: "Rate Change (%)", type: "number", hint: "% change applied to current rate (alternative to flat delta)" },
      { key: "amount", label: "Flat Rate Delta ($)", type: "number", hint: "Absolute rate change per unit (alternative to %)" },
    ],
  },
  headcount: {
    label: "Headcount",
    description:
      "Model a staffing change over a period range. The engine preserves already-active months and pro-rates cost across the supplied start–end range.",
    fields: [
      { key: "startPeriod", label: "Start Period", type: "number", hint: "First active month (1–12)", required: true },
      { key: "endPeriod", label: "End Period", type: "number", hint: "Last active month (1–12)", required: true },
      { key: "quantityDelta", label: "Headcount Delta", type: "number", hint: "Positive = adds, negative = removes (integer)", required: true },
      { key: "annualSalary", label: "Annual Salary ($)", type: "number", hint: "Annualized comp per head", required: true },
      { key: "benefitRate", label: "Benefit Rate (%)", type: "number", hint: "Benefit load as % of salary (e.g. 25 for 25%)" },
    ],
  },
  project: {
    label: "Project",
    description:
      "Introduce or remove a discrete project spend block across a period range.",
    fields: [
      { key: "glAccount", label: "GL Account", hint: "Restrict to one account (optional)" },
      { key: "startPeriod", label: "Start Period", type: "number", hint: "1–12" },
      { key: "endPeriod", label: "End Period", type: "number", hint: "1–12" },
      { key: "amount", label: "Total Amount ($)", type: "number", required: true },
      { key: "sourceReferences", label: "Source References", hint: "Project ID, budget approval reference (optional)" },
    ],
  },
  commitment: {
    label: "Commitment",
    description:
      "Register a contractual or structural commitment against a baseline GL account. Source references must point to an immutable snapshot (contract ID, PO reference) — the engine rejects source-backed commitments without them.",
    fields: [
      { key: "glAccount", label: "GL Account", hint: "Required for GL-backed commitments" },
      { key: "startPeriod", label: "Start Period", type: "number", hint: "1–12" },
      { key: "endPeriod", label: "End Period", type: "number", hint: "1–12" },
      { key: "amount", label: "Amount ($)", type: "number", required: true },
      {
        key: "sourceReferences",
        label: "Source References",
        hint: "Contract ID or PO reference — required for source-backed drivers; must be immutable snapshots",
        required: true,
      },
    ],
  },
  vendor_consolidation: {
    label: "Vendor Consolidation",
    description:
      "Model savings from consolidating vendors. Supply savings amount or percentage — not both.",
    fields: [
      { key: "glAccount", label: "GL Account", hint: "Restrict to one account (optional)" },
      { key: "startPeriod", label: "Start Period", type: "number", hint: "1–12" },
      { key: "endPeriod", label: "End Period", type: "number", hint: "1–12" },
      { key: "amount", label: "Savings Amount ($)", type: "number", hint: "Fixed dollar savings (alternative to %)" },
      { key: "percentage", label: "Savings (%)", type: "number", hint: "% savings applied to baseline (alternative to $)" },
      { key: "sourceReferences", label: "Vendor References", hint: "Vendor names or contract IDs (optional)" },
    ],
  },
  new_investment: {
    label: "New Investment",
    description:
      "Fund net-new spend not currently in the baseline. Provide an approval reference and total amount; without explicit monthly allocations, the total is spread evenly across the selected period range.",
    fields: [
      { key: "glAccount", label: "GL Account", hint: "Target GL account (optional but recommended)" },
      { key: "startPeriod", label: "Start Period", type: "number", hint: "1–12" },
      { key: "endPeriod", label: "End Period", type: "number", hint: "1–12" },
      { key: "amount", label: "Total Amount ($)", type: "number", required: true },
      {
        key: "sourceReferences",
        label: "Approval References",
        hint: "Board or executive approval reference — required",
        required: true,
      },
    ],
  },
};
